import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useStoreScope } from "@/hooks/use-store-context";
import { api } from "../runtime";
import type {
  ActivityEvent,
  Appointment,
  FollowupList,
  Offer,
  OpportunityDetail,
  OpportunityImage,
  OpportunityListItem,
  OpportunitySource,
  OpportunityStage,
  Page,
  Valuation,
} from "../types";
import { qk } from "./keys";

/** Saved views of the inbox. Appointments (incl. no-shows) and purchases have their own pages. */
export const OPPORTUNITY_VIEWS = ["all", "needs_attention", "rogue_working", "hot_seller", "offer_gap_lt_1k", "aged_7d", "approval_required"] as const;
export type OpportunityView = (typeof OPPORTUNITY_VIEWS)[number];

export const OPPORTUNITY_SORTS = ["score", "lastActivity", "created", "offer", "vehicle", "stage"] as const;
export type OpportunitySort = (typeof OPPORTUNITY_SORTS)[number];

/** Mirrors the /opportunities URL search params (shareable views). */
export interface OpportunityFilters {
  view?: OpportunityView;
  /** Also show lost and closed leads (the inbox shows active ones by default). */
  closed?: boolean;
  store?: string;
  buyer?: string;
  source?: OpportunitySource[];
  stage?: OpportunityStage[];
  scoreMin?: number;
  make?: string;
  model?: string;
  yearMin?: number;
  yearMax?: number;
  mileageMax?: number;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  q?: string;
  sort?: OpportunitySort;
  order?: "asc" | "desc";
}

/** Filters the server applies when it doesn't say (older servers). */
export const BACKEND_SUPPORTED_FILTERS = new Set(["store", "stage", "buyer"]);

export interface OpportunityPage extends Page<OpportunityListItem> {
  /** [planned] which filters the server applied. Absent → assume BACKEND_SUPPORTED_FILTERS. */
  appliedFilters?: string[];
}

const PAGE_SIZE = 50;

export function toOpportunityQuery(f: OpportunityFilters) {
  return {
    view: f.view && f.view !== "all" ? f.view : undefined,
    // Purchased leads are on the Purchases page; lost and closed ones only when asked for.
    scope: f.closed ? "pipeline" : "active",
    storeId: f.store,
    // The backend validates assignedUserId as a UUID; "no buyer" is its own [planned] flag.
    assignedUserId: f.buyer && f.buyer !== "unassigned" ? f.buyer : undefined,
    unassigned: f.buyer === "unassigned" ? true : undefined,
    source: f.source,
    stage: f.stage,
    scoreMin: f.scoreMin,
    make: f.make,
    model: f.model,
    yearMin: f.yearMin,
    yearMax: f.yearMax,
    mileageMax: f.mileageMax,
    createdFrom: f.createdFrom,
    createdTo: f.createdTo,
    updatedFrom: f.updatedFrom,
    updatedTo: f.updatedTo,
    q: f.q,
    sort: f.sort,
    order: f.sort ? (f.order ?? "desc") : undefined,
    expand: "summary",
    limit: PAGE_SIZE,
  };
}

/** Filters that are active in the UI but were not applied by the server. */
export function unappliedFilters(f: OpportunityFilters, page: OpportunityPage | undefined): string[] {
  if (!page) return [];
  const applied = new Set(page.appliedFilters ?? BACKEND_SUPPORTED_FILTERS);
  const active: string[] = [];
  for (const [key, value] of Object.entries(f)) {
    if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) continue;
    if (key === "order") continue;
    if (key === "closed") {
      if (!applied.has("scope")) active.push("closed");
      continue;
    }
    if (key === "buyer" && value === "unassigned") {
      if (!applied.has("buyer:unassigned")) active.push("buyer:unassigned");
      continue;
    }
    if (key === "view") {
      if (value !== "all" && !applied.has(`view:${value}`) && !applied.has("view")) active.push(`view:${value}`);
      continue;
    }
    if (!applied.has(key)) active.push(key);
  }
  return active;
}

export function useOpportunities(filters: OpportunityFilters) {
  const scope = useStoreScope();
  return useInfiniteQuery({
    queryKey: qk.opportunities.list(scope, filters),
    queryFn: ({ pageParam, signal }) =>
      api.get<OpportunityPage>("/v1/opportunities", {
        query: { ...toOpportunityQuery(filters), cursor: pageParam ?? undefined },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function opportunityQueryOptions(id: string) {
  return {
    queryKey: qk.opportunities.detail(id),
    queryFn: () => api.get<OpportunityDetail>(`/v1/opportunities/${id}`),
    staleTime: 10_000,
  };
}

export function useOpportunity(id: string) {
  return useQuery({ ...opportunityQueryOptions(id), enabled: Boolean(id) });
}

export function useOpportunityActivity(id: string) {
  return useQuery({
    queryKey: qk.opportunities.activity(id),
    queryFn: async () => (await api.get<{ items: ActivityEvent[] }>(`/v1/opportunities/${id}/activity`)).items,
    retry: false,
  });
}

export function useOffers(opportunityId: string) {
  return useQuery({
    queryKey: qk.opportunities.offers(opportunityId),
    queryFn: async () => (await api.get<{ items: Offer[] }>(`/v1/opportunities/${opportunityId}/offers`)).items,
  });
}

export function useValuations(opportunityId: string) {
  return useQuery({
    queryKey: qk.opportunities.valuations(opportunityId),
    queryFn: async () => (await api.get<{ items: Valuation[] }>(`/v1/opportunities/${opportunityId}/valuations`)).items,
  });
}

export function useOpportunityAppointments(opportunityId: string) {
  return useQuery({
    queryKey: qk.opportunities.appointments(opportunityId),
    queryFn: async () => (await api.get<{ items: Appointment[] }>(`/v1/opportunities/${opportunityId}/appointments`)).items,
  });
}

/** The lead's planned follow-up and the recent ones (store time zone included). */
export function useFollowups(opportunityId: string) {
  return useQuery({
    queryKey: qk.opportunities.followups(opportunityId),
    queryFn: () => api.get<FollowupList>(`/v1/opportunities/${opportunityId}/followups`),
  });
}

export function useOpportunityImages(opportunityId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.opportunities.images(opportunityId),
    queryFn: async () => (await api.get<{ items: OpportunityImage[] }>(`/v1/opportunities/${opportunityId}/images`)).items,
    enabled: opts.enabled ?? true,
    // Image URLs are signed for an hour; refetch well before they expire.
    staleTime: 5 * 60_000,
  });
}
